/**
 * Partner Dashboard Page — Full fleet management space
 * Tabs: Aperçu, Propositions, Entreprise, Chauffeurs, Véhicules, Missions
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  LogOut, Users, Car, MapPin, Loader2, RefreshCw,
  CheckCircle, Clock, Wifi, Building2, Euro, AlertTriangle,
  ArrowRight, Plane, Briefcase, UserCheck, XCircle, FileText
} from 'lucide-react';
import axios from 'axios';
import PartnerCompanyTab from './PartnerCompanyTab';
import PartnerDriversTab from './PartnerDriversTab';
import PartnerVehiclesTab from './PartnerVehiclesTab';
import PartnerWalletTab from './PartnerWalletTab';
import { PartnerMapTab } from './PartnerMapTab';
import PartnerDispatchMapPage from './PartnerDispatchMapPage';
import { PartnerMissionsTab } from './PartnerMissionsTab';
import config from 'config';

const API = `${config.API_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

const vehicleNames = {
  berline: 'Berline', van: 'Van', green: 'Green', premium: 'Premium',
  sedan: 'Berline', minivan: 'Van', suv: 'SUV',
};
const serviceNames = {
  transfer: 'Transfert', excursion: 'Excursion', mise_a_disposition: 'Mise à disposition',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return '—'; }
};

// Proposals Tab for fleet managers — detailed mission view
function ProposalsTab({ proposals, drivers, vehicles, onRefresh }) {
  const [accepting, setAccepting] = useState(null);
  const [declining, setDeclining] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState({});
  const [selectedVehicle, setSelectedVehicle] = useState({});

  const handleAccept = async (proposal) => {
    const driverId = selectedDriver[proposal.id];
    const vehicleId = selectedVehicle[proposal.id];
    if (!driverId || !vehicleId) { alert('Sélectionnez un chauffeur et un véhicule'); return; }
    setAccepting(proposal.id);
    try {
      await axios.post(
        `${API}/partner/missions/${proposal.id}/accept?source=${proposal.source}`,
        { driver_id: driverId, vehicle_id: vehicleId },
        { headers: getHeaders() }
      );
      onRefresh?.();
    } catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
    setAccepting(null);
  };

  const handleDecline = async (proposal) => {
    if (!window.confirm('Refuser cette proposition ?')) return;
    setDeclining(proposal.id);
    try {
      await axios.post(
        `${API}/partner/missions/${proposal.id}/decline?source=${proposal.source}`,
        {},
        { headers: getHeaders() }
      );
      onRefresh?.();
    } catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
    setDeclining(null);
  };

  if (proposals.length === 0) {
    return (
      <Card className="bg-[#1F1F1F] border-[#C9A24D]/20">
        <CardContent className="p-8 text-center">
          <CheckCircle className="w-10 h-10 text-[#C9A24D]/30 mx-auto mb-3" />
          <p className="text-[#E5E5E5]/40 text-sm">Aucune proposition en attente</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="proposals-tab">
      <h2 className="text-white font-semibold flex items-center gap-2 text-lg">
        <AlertTriangle className="w-5 h-5 text-amber-400" />
        Propositions en attente ({proposals.length})
      </h2>
      {proposals.map(p => (
        <Card key={p.id} className="bg-[#1F1F1F] border-[#C9A24D]/20 overflow-hidden" data-testid={`proposal-${p.id}`}>
          <CardContent className="p-0">
            {/* Header with fee */}
            <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-[#C9A24D]/10">
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${p.type === 'EXCURSION' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'}`}>{p.type}</span>
                <span className="text-white font-mono text-sm font-medium">{p.ref_id || p.id.slice(0, 8)}</span>
                {p.service_type && (
                  <span className="text-[#E5E5E5]/30 text-[10px]">{serviceNames[p.service_type] || p.service_type}</span>
                )}
              </div>
              <div className="text-right">
                <p className="text-[10px] text-[#E5E5E5]/40">Tarif proposé</p>
                <p className="text-[#C9A24D] text-xl font-bold" data-testid={`proposal-fee-${p.id}`}>{p.proposed_fee} €</p>
              </div>
            </div>

            <div className="p-4 space-y-3">
              {/* Trajet */}
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <div className="flex flex-col items-center mt-1">
                    <div className="w-2 h-2 rounded-full bg-emerald-500" />
                    <div className="w-0.5 h-6 bg-[#333]" />
                    <div className="w-2 h-2 rounded-full bg-red-500" />
                  </div>
                  <div className="flex-1 min-w-0 space-y-2">
                    <div>
                      <p className="text-[#E5E5E5]/40 text-[10px]">Prise en charge</p>
                      <p className="text-white text-sm">{p.pickup_address || '—'}</p>
                    </div>
                    <div>
                      <p className="text-[#E5E5E5]/40 text-[10px]">Dépose</p>
                      <p className="text-white text-sm">{p.dropoff_address || '—'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Détails mission */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 bg-[#0B0B0B]/50 rounded-lg p-3">
                <div>
                  <p className="text-[#E5E5E5]/30 text-[10px]">Date & heure</p>
                  <p className="text-white text-xs font-medium flex items-center gap-1">
                    <Clock className="w-3 h-3 text-[#C9A24D]" />{fmtDate(p.start_time)}
                  </p>
                </div>
                <div>
                  <p className="text-[#E5E5E5]/30 text-[10px]">Passagers</p>
                  <p className="text-white text-xs font-medium flex items-center gap-1">
                    <Users className="w-3 h-3 text-[#C9A24D]" />{p.passenger_count || 1}
                  </p>
                </div>
                <div>
                  <p className="text-[#E5E5E5]/30 text-[10px]">Véhicule requis</p>
                  <p className="text-white text-xs font-medium flex items-center gap-1">
                    <Car className="w-3 h-3 text-[#C9A24D]" />{vehicleNames[p.vehicle_category] || p.vehicle_category || '—'}
                  </p>
                </div>
                <div>
                  <p className="text-[#E5E5E5]/30 text-[10px]">Bagages</p>
                  <p className="text-white text-xs font-medium flex items-center gap-1">
                    <Briefcase className="w-3 h-3 text-[#C9A24D]" />{p.luggage || 0}
                  </p>
                </div>
                {p.flight_number && (
                  <div>
                    <p className="text-[#E5E5E5]/30 text-[10px]">Vol</p>
                    <p className="text-white text-xs font-medium flex items-center gap-1">
                      <Plane className="w-3 h-3 text-[#C9A24D]" />{p.flight_number}
                    </p>
                  </div>
                )}
                {p.is_round_trip && (
                  <div>
                    <p className="text-[#E5E5E5]/30 text-[10px]">Retour</p>
                    <p className="text-amber-400 text-xs font-medium">Aller-retour</p>
                  </div>
                )}
              </div>

              {/* Notes */}
              {(p.special_requests || p.proposal_note) && (
                <div className="bg-amber-500/5 border border-amber-500/15 rounded-lg p-2.5">
                  {p.proposal_note && (
                    <p className="text-amber-400 text-xs"><span className="font-semibold">Note dispatch :</span> {p.proposal_note}</p>
                  )}
                  {p.special_requests && (
                    <p className="text-[#E5E5E5]/50 text-xs mt-1"><span className="font-semibold text-[#E5E5E5]/60">Demandes spéciales :</span> {p.special_requests}</p>
                  )}
                </div>
              )}

              {/* Sélection chauffeur + véhicule */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-[#333]">
                <div>
                  <label className="text-[10px] text-[#E5E5E5]/40 mb-1 block flex items-center gap-1">
                    <UserCheck className="w-3 h-3" /> Chauffeur *
                  </label>
                  <select value={selectedDriver[p.id] || ''} onChange={e => setSelectedDriver(prev => ({ ...prev, [p.id]: e.target.value }))}
                    className="w-full h-9 text-xs bg-[#0B0B0B] border border-[#C9A24D]/20 rounded-lg text-white px-2 focus:border-[#C9A24D] focus:outline-none"
                    data-testid={`proposal-driver-${p.id}`}>
                    <option value="">Sélectionner un chauffeur</option>
                    {drivers.map(d => <option key={d.id} value={d.id}>{d.first_name} {d.last_name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-[#E5E5E5]/40 mb-1 block flex items-center gap-1">
                    <Car className="w-3 h-3" /> Véhicule *
                  </label>
                  <select value={selectedVehicle[p.id] || ''} onChange={e => setSelectedVehicle(prev => ({ ...prev, [p.id]: e.target.value }))}
                    className="w-full h-9 text-xs bg-[#0B0B0B] border border-[#C9A24D]/20 rounded-lg text-white px-2 focus:border-[#C9A24D] focus:outline-none"
                    data-testid={`proposal-vehicle-${p.id}`}>
                    <option value="">Sélectionner un véhicule</option>
                    {vehicles.map(v => <option key={v.id || v.license_plate} value={v.license_plate}>{v.brand || v.name} — {v.license_plate}</option>)}
                  </select>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-2 pt-1">
                <Button onClick={() => handleAccept(p)}
                  disabled={accepting === p.id || !selectedDriver[p.id] || !selectedVehicle[p.id]}
                  className="flex-1 bg-[#C9A24D] hover:bg-[#b8913f] text-black font-semibold disabled:opacity-40 h-10 text-xs sm:text-sm"
                  data-testid={`accept-proposal-${p.id}`}>
                  {accepting === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle className="w-4 h-4 mr-2" />Accepter</>}
                </Button>
                <Button onClick={() => handleDecline(p)}
                  disabled={declining === p.id}
                  variant="outline"
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-10 text-xs sm:text-sm"
                  data-testid={`decline-proposal-${p.id}`}>
                  {declining === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><XCircle className="w-4 h-4 mr-1" />Refuser</>}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function PartnerDashboardPage() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [dashRes, driversRes, vehiclesRes, proposalsRes] = await Promise.all([
        axios.get(`${API}/partner/dashboard`, { headers: getHeaders() }).catch(() => ({ data: null })),
        axios.get(`${API}/partner/drivers`, { headers: getHeaders() }).catch(() => ({ data: { drivers: [] } })),
        axios.get(`${API}/partner/vehicles`, { headers: getHeaders() }).catch(() => ({ data: { vehicles: [] } })),
        axios.get(`${API}/partner/missions/proposals`, { headers: getHeaders() }).catch(() => ({ data: { missions: [] } })),
      ]);
      if (dashRes.data) setDashboard(dashRes.data);
      else setError("Aucun partenaire associé à ce compte");
      setDrivers(driversRes.data?.drivers || []);
      setVehicles(vehiclesRes.data?.vehicles || []);
      setProposals(proposalsRes.data?.missions || []);
    } catch {
      setError("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  const handleLogout = async () => { await logout(); navigate('/login'); };

  if (loading) {
    return <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#C9A24D] animate-spin" /></div>;
  }
  if (error && !dashboard) {
    return (
      <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center text-center">
        <div>
          <Building2 className="w-12 h-12 text-[#444] mx-auto mb-3" />
          <p className="text-[#888] mb-4">{error}</p>
          <Button onClick={handleLogout} variant="outline" className="border-[#C9A24D]/30 text-[#C9A24D]"><LogOut className="w-4 h-4 mr-2" /> Déconnexion</Button>
        </div>
      </div>
    );
  }

  const tabs = [
    { key: 'overview', label: 'Aperçu', icon: Briefcase },
    { key: 'map', label: 'Carte', icon: MapPin },
    { key: 'missions', label: 'Missions', icon: FileText },
    { key: 'proposals', label: 'Propositions', count: proposals.length, icon: Clock },
    { key: 'wallet', label: 'Wallet', icon: Euro },
    { key: 'company', label: 'Entreprise', icon: Building2 },
    { key: 'drivers', label: 'Chauffeurs', count: drivers.length, icon: Users },
    { key: 'vehicles', label: 'Véhicules', icon: Car },
  ];

  return (
    <div className="h-screen flex bg-[#0B0B0B] overflow-hidden" data-testid="partner-dashboard">
      {/* Left sidebar */}
      <aside className="w-16 lg:w-56 shrink-0 bg-[#1F1F1F] border-r border-[#C9A24D]/15 flex flex-col">
        <div className="h-14 sm:h-16 flex items-center gap-2 px-3 lg:px-5 border-b border-[#C9A24D]/15 shrink-0">
          <h1 className="font-['Playfair_Display'] text-xl font-bold text-[#C9A24D]">Y<span className="hidden lg:inline"> GO</span></h1>
        </div>
        <div className="px-5 py-2 border-b border-[#C9A24D]/10 hidden lg:block shrink-0">
          <span className="text-[#E5E5E5]/55 text-xs truncate block">{dashboard?.partner_name || 'Espace Flotte'}</span>
        </div>
        <nav className="flex-1 overflow-y-auto py-2 scrollbar-none">
          {tabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} title={t.label}
              className={`w-full flex items-center gap-3 px-3 lg:px-5 py-2.5 text-sm font-medium border-l-2 transition ${tab === t.key ? 'border-[#C9A24D] text-[#C9A24D] bg-[#C9A24D]/5' : 'border-transparent text-[#E5E5E5]/50 hover:text-white hover:bg-white/5'}`}
              data-testid={`tab-${t.key}`}>
              <t.icon className="w-4 h-4 shrink-0" />
              <span className="hidden lg:inline">{t.label}</span>
              {t.count > 0 && <span className="ml-auto text-[10px] bg-[#C9A24D]/20 text-[#C9A24D] px-1.5 rounded hidden lg:inline">{t.count}</span>}
            </button>
          ))}
        </nav>
        <div className="border-t border-[#C9A24D]/15 p-2 flex flex-col gap-1 shrink-0">
          <Button variant="ghost" size="sm" onClick={fetchData} className="text-[#E5E5E5]/60 hover:text-[#C9A24D] justify-start gap-3 px-2 lg:px-3">
            <RefreshCw className="w-4 h-4 shrink-0" /><span className="hidden lg:inline">Actualiser</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-[#E5E5E5]/60 hover:text-[#C9A24D] justify-start gap-3 px-2 lg:px-3" data-testid="logout-btn">
            <LogOut className="w-4 h-4 shrink-0" /><span className="hidden lg:inline">Déconnexion</span>
          </Button>
        </div>
      </aside>

      {/* Right content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {tab === 'map' ? (
          <div className="flex-1 overflow-hidden" data-testid="partner-map-fullpage">
            <PartnerDispatchMapPage />
          </div>
        ) : (
          <main className="flex-1 overflow-y-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
            {/* Overview */}
            {tab === 'overview' && (
              <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4" data-testid="overview-stats">
                {[
                  { label: 'Chauffeurs', value: dashboard?.driver_count || 0, icon: Users, color: 'text-[#E5E5E5]' },
                  { label: 'Actifs', value: dashboard?.active_drivers || 0, icon: CheckCircle, color: 'text-green-400' },
                  { label: 'En ligne', value: dashboard?.online_drivers || 0, icon: Wifi, color: 'text-[#C9A24D]' },
                  { label: 'Véhicules', value: dashboard?.vehicle_count || 0, icon: Car, color: 'text-[#E5E5E5]' },
                ].map(s => (
                  <Card key={s.label} className="bg-[#1F1F1F] border-[#C9A24D]/10">
                    <CardContent className="p-3 sm:p-4 flex items-center justify-between">
                      <div><p className="text-[#E5E5E5]/60 text-[10px] sm:text-xs">{s.label}</p><p className={`text-xl sm:text-2xl font-semibold ${s.color}`}>{s.value}</p></div>
                      <s.icon className="w-6 h-6 sm:w-8 sm:h-8 text-[#C9A24D]/40" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {tab === 'proposals' && <ProposalsTab proposals={proposals} drivers={drivers} vehicles={vehicles} onRefresh={fetchData} />}
            {tab === 'missions' && <PartnerMissionsTab />}
            {tab === 'wallet' && <PartnerWalletTab />}
            {tab === 'company' && <PartnerCompanyTab onRefresh={fetchData} />}
            {tab === 'drivers' && <PartnerDriversTab drivers={drivers} onRefresh={fetchData} />}
            {tab === 'vehicles' && <PartnerVehiclesTab />}
          </main>
        )}
      </div>
    </div>
  );
}
